/**
 * "Koble til <provider>" — the identity-menu affordance to link an external
 * IdP account (Google, Facebook, …) to the caller's OWN, already-logged-in
 * deSjeff account (same `sub` → cookbooks/recipes preserved; see
 * infra/zitadel/CONNECT-GOOGLE-SPEC.md). Renders inside TopBar's identity
 * menu, so it only mounts (and only checks link status) while that menu is
 * open — no background polling. One instance is rendered per configured
 * provider (see identity/providers.ts).
 *
 * Feature flag: TopBar only renders this control for providers whose idpId
 * is configured — `provider.idpId` is therefore always non-empty here.
 *
 * Security note: this component NEVER renders the access token, the external
 * `sub`, or any Zitadel intent token — only a connected/not-connected chip
 * and plain-language status text.
 */

import React, { useEffect, useState } from "react";

import { IdpLinkUnauthorizedError, isIdpLinked, startIdpLinkIntent } from "../identity/idpLink";
import type { LinkProviderConfig } from "../identity/providers";

/** The SPA-owned route Zitadel redirects back to after a given provider (both success and failure). */
export function idpLinkCallbackPath(providerKey: string): string {
  return `/settings/connections/${providerKey}/callback`;
}

/** Where to resume after the round-trip through the external IdP — read by the callback page. */
export const IDP_LINK_RETURN_TO_KEY = "desjeff.idpLinkReturnTo";

type LinkState =
  | { status: "checking" }
  | { status: "not-linked" }
  | { status: "linked" }
  | { status: "starting" }
  | { status: "relogin" }
  | { status: "error"; message: string };

export function IdpLinkControl({
  subject,
  accessToken,
  provider,
  onSessionExpired,
}: {
  subject: string;
  accessToken: string;
  provider: LinkProviderConfig;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { key, idpId, connectLabel, displayName } = provider;
  const [state, setState] = useState<LinkState>({ status: "checking" });

  // Runs once per mount — the parent only mounts this while the identity menu
  // is open, so this is naturally "check on open", not a background poll.
  useEffect(() => {
    let ignore = false;
    isIdpLinked(subject, idpId, accessToken)
      .then((linked) => {
        if (!ignore) setState({ status: linked ? "linked" : "not-linked" });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof IdpLinkUnauthorizedError) {
          setState({ status: "relogin" });
        } else {
          setState({
            status: "error",
            message:
              err instanceof Error ? err.message : `Kunne ikke sjekke ${displayName}-status.`,
          });
        }
      });
    return () => {
      ignore = true;
    };
    // Intentionally run once on mount: props are stable for the component's
    // lifetime (it is remounted, not updated, each time the identity menu is
    // reopened — see the empty deps array).
  }, []);

  function handleConnect(): void {
    setState({ status: "starting" });
    const callbackUrl = `${window.location.origin}${idpLinkCallbackPath(key)}`;
    // Remember where the user was so the callback page can send them back.
    window.sessionStorage.setItem(IDP_LINK_RETURN_TO_KEY, window.location.pathname);
    startIdpLinkIntent(idpId, callbackUrl, callbackUrl, accessToken)
      .then((result) => {
        window.location.assign(result.authUrl);
      })
      .catch((err: unknown) => {
        if (err instanceof IdpLinkUnauthorizedError) {
          setState({ status: "relogin" });
        } else {
          setState({
            status: "error",
            message:
              err instanceof Error ? err.message : `Kunne ikke starte ${displayName}-kobling.`,
          });
        }
      });
  }

  return (
    <div className="idp-link" data-testid={`idp-link-${key}`}>
      {state.status === "checking" && (
        <p className="idp-link-status" data-testid={`idp-link-${key}-checking`}>
          Sjekker {displayName}-kobling …
        </p>
      )}

      {state.status === "linked" && (
        <p className="idp-link-status" data-testid={`idp-link-${key}-connected`}>
          <span className="chip clay">{displayName} tilkoblet</span>
        </p>
      )}

      {state.status === "not-linked" && (
        <button
          type="button"
          className="link-btn identity-menu-link idp-link-btn"
          data-testid={`idp-link-${key}-connect`}
          onClick={handleConnect}
        >
          {connectLabel}
        </button>
      )}

      {state.status === "starting" && (
        <button
          type="button"
          className="link-btn identity-menu-link idp-link-btn is-busy"
          data-testid={`idp-link-${key}-starting`}
          disabled
        >
          Kobler til …
        </button>
      )}

      {state.status === "relogin" && (
        <div className="idp-link-relogin" data-testid={`idp-link-${key}-relogin`}>
          <p className="idp-link-status">Logg inn på nytt for å koble til {displayName}.</p>
          <button
            type="button"
            className="link-btn identity-menu-link idp-link-btn"
            data-testid={`idp-link-${key}-relogin-btn`}
            onClick={onSessionExpired}
          >
            Logg inn på nytt
          </button>
        </div>
      )}

      {state.status === "error" && (
        <p className="feed-error idp-link-status" data-testid={`idp-link-${key}-error`}>
          {state.message}
        </p>
      )}
    </div>
  );
}
